import { incrementUserValue } from "../auth/incrementUserValue";
import { updateCurrentUserInfo } from "../auth/updateCurrentUserInfo";
import { isEmpty } from "./utilities";

export const checkBadges = async (
  userData,
  isVerified,
  workoutTime,
  currentActivity,
  userActivity
) => {
  const { allWorkouts, verifiedWorkouts, completedTrail, sumTime, streak } = userData;
  const badgeNumbers = [1, 3, 5, 10, 15, 20, 30, 50, 70, 100, 200];
  const currentDate = new Date().toISOString().split("T")[0];

  await incrementUserValue("allWorkouts");
  isVerified && (await incrementUserValue("verifiedWorkouts"));

  const allWorkoutsResult = checkAllWorkoutsBadge(allWorkouts, badgeNumbers, currentDate);
  const allVerfifiedWorkoutsResult = checkVerifiedWorkoutsBadge(
    verifiedWorkouts,
    badgeNumbers,
    currentDate,
    isVerified
  );
  const allSumTimeBadge = checkSumTimeBadge(sumTime, workoutTime, currentDate);
  const streakBadge = await checkStreak(streak, currentDate);
  const completedTrailBadge = await checkCompletedTrail(
    badgeNumbers,
    currentActivity,
    currentDate,
    completedTrail,
    userActivity
  );

  const allBadges = [
    allWorkoutsResult,
    allVerfifiedWorkoutsResult,
    ...allSumTimeBadge,
    streakBadge,
    completedTrailBadge,
  ];

  const newBadges = allBadges.filter((badge) => badge !== null);

  return newBadges;
};

export const checkAllWorkoutsBadge = (allWorkoutsFromDb, badgeNumbers, currentDate) => {
  const allWorkouts = allWorkoutsFromDb || 0;
  if (badgeNumbers.indexOf((allWorkouts || 0) + 1) + 1) {
    const badgeName = `allWorkouts-${allWorkouts + 1}`;
    updateCurrentUserInfo({ [`badges.${badgeName}`]: currentDate });

    return {
      open: true,
      title: "Gesamte Workouts",
      text: "Du hast eine neue Höchstanzahl an Workouts erreicht: " + (allWorkouts + 1) + " Mal",
      id: 1,
      amount: allWorkouts + 1,
      unit: "Mal",
    };
  }
  return null;
};

export const checkVerifiedWorkoutsBadge = (
  verifiedWorkoutsFromDb,
  badgeNumbers,
  currentDate,
  isVerified
) => {
  const verifiedWorkouts = verifiedWorkoutsFromDb || 0;
  if (badgeNumbers.indexOf(verifiedWorkouts + 1) + 1 && isVerified) {
    const badgeName = `verifiedWorkouts-${verifiedWorkouts + 1}`;
    updateCurrentUserInfo({ [`badges.${badgeName}`]: currentDate });
    return {
      open: true,
      title: "Verifizierte Workouts",
      text:
        "Du hast eine neue Höchstanzahl an verifizierten Workouts erreicht: " +
        (verifiedWorkouts + 1),
      id: 2,
      amount: verifiedWorkouts + 1,
      unit: "verifizierte Workouts",
    };
  }
  return null;
};

export const checkSumTimeBadge = (timeFromDB, workoutTime, currentDate) => {
  const calculatedTime = (parseInt(timeFromDB) || 0) + parseInt(workoutTime);

  updateCurrentUserInfo({ sumTime: calculatedTime });

  const timeIntervals = [
    120, 300, 600, 900, 1800, 2700, 3600, 7200, 10800, 14400, 18000, 36000, 72000, 108000,
  ];

  const unlockdIntervals = timeIntervals.filter(
    (intervalNumber) => intervalNumber > timeFromDB && intervalNumber <= calculatedTime
  );

  let newDBBadges = {};
  unlockdIntervals.forEach((interval) => {
    const badgeName = `sumTime-${interval}`;
    newDBBadges[`badges.${badgeName}`] = currentDate;
  });

  !isEmpty(newDBBadges) && updateCurrentUserInfo(newDBBadges);

  const badgeContentArr = unlockdIntervals.map((interval) => ({
    open: true,
    text: `Du hast mehr als ${interval / 60} Minuten Sport gemacht!`,
    title: "Zeit im Workout",
    id: 3,
    amount: interval / 60,
    unit: "Minuten",
  }));

  return badgeContentArr;
};

export const checkStreak = async (streak = {}, currentDate) => {
  const streakIntervals = [2, 5, 7, 10, 14, 21, 42, 70, 140, 210, 280, 365];
  const { date: dateFromDB, daysInARow: daysInARowFromDb } = streak;
  const daysInARow = daysInARowFromDb || 0;

  let yesterday = new Date();
  yesterday.setDate(new Date(currentDate).getDate() - 1);
  yesterday = yesterday.toISOString().split("T")[0];

  const isStreak = dateFromDB === yesterday;

  if (isStreak) {
    updateCurrentUserInfo({ streak: { date: currentDate, daysInARow: daysInARow + 1 } });

    if (streakIntervals.includes(daysInARow + 1)) {
      const badgeName = `streak-${daysInARow + 1}`;
      updateCurrentUserInfo({ [`badges.${badgeName}`]: currentDate });
      return {
        open: true,
        text: `Du hast ${daysInARow + 1} Tage in Folge trainiert!`,
        title: "Streak",
        id: 4,
        amount: daysInARow + 1,
        unit: "Tage",
      };
    }
  } else if (dateFromDB !== currentDate) {
    updateCurrentUserInfo({ streak: { date: currentDate, daysInARow: 1 } });
  }
  return null;
};

export const checkCompletedTrail = async (
  badgeNumbers,
  currentActivity,
  currentDate,
  completedTrail = {},
  recentActivity
) => {
  const { date: dateFromDB, numberOfCompletions: completionFromDb } = completedTrail;
  const numberOfCompletions = completionFromDb || 0;

  let currentDayExercises = currentActivity?.exercises;
  if (!currentDayExercises) return null;

  const currentDayWithRecentActivity = [...currentDayExercises, recentActivity];

  const distinctStNrSet = [...new Set(currentDayWithRecentActivity.map((ex) => ex.stNr))];

  const alreadyCompleted = currentDate === dateFromDB;

  if (distinctStNrSet.length === 9 && !alreadyCompleted) {
    updateCurrentUserInfo({
      completedTrail: {
        date: currentDate,
        numberOfCompletions: numberOfCompletions + 1,
      },
    });

    if (badgeNumbers.indexOf(numberOfCompletions + 1) + 1) {
      const badgeName = `completedTrail-${numberOfCompletions + 1}`;
      updateCurrentUserInfo({ [`badges.${badgeName}`]: currentDate });
      return {
        open: true,
        text: `Du hast ${numberOfCompletions + 1} Mal alle Stationen abgeschlossen!`,
        title: "Gesamter Trail",
        id: 5,
        amount: numberOfCompletions + 1,
        unit: "Mal",
      };
    }
  }

  return null;
};

export const checkLikeBadges = (givenLikes) => {
  const badgeNumbers = [1, 5, 10, 25, 50, 70, 100];
  const currentDate = new Date().toISOString().split("T")[0];

  const allLikes = givenLikes || 0;

  if (badgeNumbers.indexOf((allLikes || 0) + 1) + 1) {
    const badgeName = `likes-${allLikes + 1}`;
    updateCurrentUserInfo({ [`badges.${badgeName}`]: currentDate });

    const result = {
      open: true,
      title: "Workout Likes",
      text: "Du hast deinen Freunden schon " + (allLikes + 1) + " Likes gegeben!",
      amount: allLikes + 1,
      unit: "Mal",
      id: 7,
    };

    return result;
  }
  return null;
};

export const checkFollowBadge = (followingAmount) => {
  const badgeNumbers = [1, 2, 5, 10, 15, 20, 30];
  const currentDate = new Date().toISOString().split("T")[0];

  const allFriends = followingAmount || 0;

  if (badgeNumbers.indexOf((allFriends || 0) + 1) + 1) {
    const badgeName = `followings-${allFriends + 1}`;
    updateCurrentUserInfo({ [`badges.${badgeName}`]: currentDate });

    const result = {
      open: true,
      title: "Neue Freunde",
      text: "Du hast neue Freunde in der App!",
      amount: allFriends + 1,
      unit: "Freunde",
      id: 6,
    };

    return result;
  }
  return null;
};
